// =============================================================================
// LIGA AJUPAM — Ventana del CTA "leé el reglamento" (v6.11.68)
// =============================================================================
// Los primeros días con la app entera habilitada son los que más errores caros
// producen: cargar el resultado tarde, cancelar sobre la hora, jugar sin
// registrar el partido. Todo eso está en el reglamento y casi nadie lo abre.
//
// ¿DESDE CUÁNDO SE CUENTAN LOS 5 DÍAS?
// -----------------------------------
// Desde que la app quedó **entera** habilitada para ESA persona, que no es lo
// mismo que el arranque de la liga: alguien que se inscribe tarde (la 5ta tiene
// inscripción tardía abierta) recibe la app completa el día que se inscribe, no
// el 01/08. Por eso el ancla es:
//
//     max(arranque de la fase regular de su liga, alta de su equipo)
//
// Se descartaron dos alternativas:
//   · `rulebook_acceptances.accepted_at` sería el ancla ideal —es por persona y
//     por liga— pero está firmado por 45 de 620 jugadores activos de la 5ta: el
//     gate de re-aceptación está dormido para los "grandfathered". Anclar ahí
//     dejaría al 93% sin CTA.
//   · Guardar un timestamp propio la primera vez que la app resuelve como
//     habilitada: es lo más exacto, pero necesita escritura desde el render y
//     una tabla nueva para algo que se deduce de datos que ya existen.
//
// Y no alcanza con la fecha: mientras el jugador tenga secciones candadeadas
// (pre-temporada, lista de espera, bloqueo por deuda o sin compañero) la app NO
// está entera habilitada, así que el CTA no corresponde todavía. Esa decisión
// ya la toma `utils/sectionAccess` y acá se recibe hecha.
//
// -----------------------------------------------------------------------------
// v6.11.75 — La ventana la puede fijar el admin
// -----------------------------------------------------------------------------
// Los 5 días eran una suposición del desarrollo, no una decisión de la
// organización. Ahora hay tres campos en `league_settings`
// (`rulebook_cta_enabled`, `rulebook_cta_start_date`, `rulebook_cta_end_date`) y
// el orden de decisión es:
//
//   1. Apagado por el admin        → no se muestra, punto.
//   2. Con alguna fecha cargada    → manda la ventana del admin (modo MANUAL).
//   3. Sin ninguna fecha cargada   → sigue el automático de 5 días (modo AUTO).
//
// El fallback al automático no es pereza: es lo que hace que una edición nueva
// que nadie configuró se comporte como hasta ahora en vez de apagarse sola. Y el
// automático conserva algo que las fechas fijas necesariamente pierden — al que
// se inscribe tarde le cuenta desde SU alta, no desde el arranque de la liga.
// Por eso el admin elige entre los dos y no se le impone uno.
//
// Lo que el admin NO puede saltear es el candado de secciones: aunque la ventana
// esté abierta, a alguien que todavía no puede usar la app entera no se le pide
// que lea el reglamento. Esa condición es de la persona, no del calendario.

const ART_TZ_OFFSET = '-03:00';
const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 5;

/** Medianoche argentina del día SIGUIENTE: el fin del admin es inclusive. */
function parseArtDayEnd(iso: string | null | undefined): Date | null {
  const start = parseArtDayStart(iso);
  return start ? new Date(start.getTime() + DAY_MS) : null;
}

/** Medianoche argentina de una fecha DATE. `null` si no es usable. */
function parseArtDayStart(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const dateOnly = String(iso).split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
  const parsed = new Date(`${dateOnly}T00:00:00${ART_TZ_OFFSET}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Timestamp completo (el alta del equipo viene con hora). */
function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const parsed = new Date(String(iso));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export interface RulebookCtaWindowInput {
  /** `regular_phase_start_date` de LA LIGA DEL EQUIPO (son 3, con arranques distintos). */
  regularPhaseStartDate?: string | null;
  /** `teams.created_at` — cuándo se inscribió esta pareja. */
  teamCreatedAt?: string | null;
  /** true mientras Tabla/Partidos sigan candadeadas para esta persona. */
  seasonSectionsLocked?: boolean | null;
  /** false mientras settings/equipo no llegaron: no se adivina. */
  dataLoaded: boolean;
  nowMs: number;
  /** `rulebook_cta_enabled`. `false` apaga el aviso. undefined = encendido. */
  ctaEnabled?: boolean | null;
  /** `rulebook_cta_start_date`. Con esta o la de fin, manda el admin. */
  ctaStartDate?: string | null;
  /** `rulebook_cta_end_date`, inclusive (se muestra todo ese día). */
  ctaEndDate?: string | null;
}

export interface RulebookCtaWindow {
  visible: boolean;
  /**
   * 1 = primer día con la app entera … 5 = último. `null` fuera de ventana y
   * también en modo manual, donde el "día N de 5" no significa nada.
   */
  dayNumber: number | null;
  /** Días enteros que le quedan al CTA. `null` fuera de ventana y en manual. */
  daysLeft: number | null;
  /** Qué decidió la visibilidad. Sirve para explicarlo en el panel del admin. */
  mode: 'auto' | 'manual' | 'off';
}

const HIDDEN_AUTO: RulebookCtaWindow = { visible: false, dayNumber: null, daysLeft: null, mode: 'auto' };
const HIDDEN_MANUAL: RulebookCtaWindow = { visible: false, dayNumber: null, daysLeft: null, mode: 'manual' };
const OFF: RulebookCtaWindow = { visible: false, dayNumber: null, daysLeft: null, mode: 'off' };

export function computeRulebookCtaWindow(input: RulebookCtaWindowInput): RulebookCtaWindow {
  const {
    regularPhaseStartDate, teamCreatedAt, seasonSectionsLocked, dataLoaded, nowMs,
    ctaEnabled, ctaStartDate, ctaEndDate,
  } = input;

  // Sin datos no se muestra nada. Un CTA que aparece y desaparece mientras
  // cargan los settings es peor que uno que llega medio segundo tarde.
  if (!dataLoaded) return HIDDEN_AUTO;

  // El interruptor del admin gana sobre todo lo demás.
  if (ctaEnabled === false) return OFF;

  // Con secciones candadeadas la app todavía NO está entera habilitada. Vale
  // también en manual: el admin decide el calendario, no a quién se le pide leer
  // el reglamento antes de poder usar la app.
  if (seasonSectionsLocked) return ctaStartDate || ctaEndDate ? HIDDEN_MANUAL : HIDDEN_AUTO;

  // ── Modo manual: alcanza con que el admin haya cargado UNA de las dos ──────
  // Con sólo inicio, queda abierto hacia adelante; con sólo fin, desde siempre
  // hasta esa fecha. Es lo que se espera de un rango con un extremo vacío, y
  // evita obligar a cargar una fecha que a nadie le importa.
  if (ctaStartDate || ctaEndDate) {
    const desde = parseArtDayStart(ctaStartDate);
    const hasta = parseArtDayEnd(ctaEndDate);
    if (desde && nowMs < desde.getTime()) return HIDDEN_MANUAL;
    if (hasta && nowMs >= hasta.getTime()) return HIDDEN_MANUAL;
    return { visible: true, dayNumber: null, daysLeft: null, mode: 'manual' };
  }

  // ── Modo automático: los 5 días de siempre ────────────────────────────────
  const phaseStart = parseArtDayStart(regularPhaseStartDate);
  const teamStart = parseInstant(teamCreatedAt);

  // Sin ninguna de las dos referencias no hay desde cuándo contar.
  if (!phaseStart && !teamStart) return HIDDEN_AUTO;

  // El más TARDÍO de los dos: es el momento en que se juntaron las dos
  // condiciones (la liga arrancó y esta pareja existe).
  const anchorMs = Math.max(phaseStart?.getTime() ?? 0, teamStart?.getTime() ?? 0);

  const elapsed = nowMs - anchorMs;
  if (elapsed < 0 || elapsed >= WINDOW_DAYS * DAY_MS) return HIDDEN_AUTO;

  return {
    visible: true,
    dayNumber: Math.floor(elapsed / DAY_MS) + 1,
    daysLeft: WINDOW_DAYS - (Math.floor(elapsed / DAY_MS) + 1),
    mode: 'auto',
  };
}
